import { STORE_PACKAGE_ID, STORE_PROJECT_ID } from "Constants";
import { GroupUpdatesMessage } from "Types";
import { getWebsocketProtocolHost } from "./utils";

const RETRY_INTERVAL = 5 * 1000;
const MAX_RETRIES = 100;

type Listener = (event: MessageEvent) => void;

class WebSocketManager {
  url: string;
  websocket: WebSocket | null;
  retryIntervalId: NodeJS.Timeout | null = null;
  defaultMessageListener: Listener | undefined = undefined;
  listeners: Record<keyof WebSocketEventMap, Array<Listener>> = {
    close: [],
    error: [],
    message: [],
    open: [],
  };
  closedByClient = false;

  constructor(url: string, defaultMessageListener?: Listener) {
    this.url = url;
    this.websocket = null;
    this.retryIntervalId = null;
    this.defaultMessageListener = defaultMessageListener;
    this.connect();
  }

  connect(): void {
    this.closedByClient = true;

    this.websocket?.close();
    this.websocket = new WebSocket(this.url);

    this.websocket.addEventListener("error", () => {
      if (this.retryIntervalId) clearInterval(this.retryIntervalId);

      this.reconnect();
    });

    this.websocket.addEventListener("close", () => {
      if (!this.closedByClient) {
        if (this.retryIntervalId) clearInterval(this.retryIntervalId);

        this.reconnect();
      }
    });

    this.websocket.addEventListener("open", () => {
      this.closedByClient = false;

      if (this.retryIntervalId) {
        clearInterval(this.retryIntervalId);
      }

      // Restore listeners
      Object.entries(this.listeners).forEach(([eventType, listeners]) => {
        listeners.forEach((listener) =>
          this.websocket?.addEventListener(
            eventType as keyof WebSocketEventMap,
            listener,
          ),
        );
      });
    });

    if (this.defaultMessageListener)
      this.websocket.addEventListener("message", this.defaultMessageListener);
  }

  reconnect(): void {
    let retries = 0;

    if (this.retryIntervalId) {
      clearInterval(this.retryIntervalId);
    }

    this.retryIntervalId = setInterval(() => {
      this.connect();

      retries++;
      if (retries >= MAX_RETRIES && this.retryIntervalId) {
        clearInterval(this.retryIntervalId);
      }
    }, RETRY_INTERVAL);
  }

  destroyConnection(): void {
    this.websocket?.close();
    this.websocket = null;
  }

  updateUrl(url: string): void {
    if (this.url === url) {
      return;
    }

    this.destroyConnection();

    this.url = url;
    this.connect();
  }

  addEventListener(
    eventType: keyof WebSocketEventMap,
    listener: Listener,
  ): void {
    this.listeners[eventType].push(listener);
    this.websocket?.addEventListener(eventType, listener);
  }

  removeEventListener(
    eventType: keyof WebSocketEventMap,
    listener: Listener,
  ): void {
    this.listeners[eventType] = this.listeners[eventType].filter(
      (item) => item !== listener,
    );
    this.websocket?.removeEventListener(eventType, listener);
  }
}

let groupUpdates: WebSocketManager | null = null;
let userUpdates: WebSocketManager | null = null;

function createUserConnection(): void {
  const url = `${getWebsocketProtocolHost()}/websocket/current-user/updates/`;

  userUpdates = new WebSocketManager(url);
}

function createGroupConnection(groupId: string): void {
  const url = `${getWebsocketProtocolHost()}/websocket/groups/${groupId}/updates/`;

  if (groupUpdates) {
    groupUpdates.updateUrl(url);
  } else {
    groupUpdates = new WebSocketManager(url, (event: MessageEvent) => {
      const message = JSON.parse(event.data) as GroupUpdatesMessage;

      if (message.entity === "package" && message.action === "deleted") {
        const storedPackageId = localStorage.getItem(STORE_PACKAGE_ID);

        if (
          message.keys.some(
            (deletedPackageId) => deletedPackageId === storedPackageId,
          )
        ) {
          localStorage.removeItem(STORE_PACKAGE_ID);
        }
      } else if (message.entity === "project" && message.action === "deleted") {
        const storedProjectId = localStorage.getItem(STORE_PROJECT_ID);

        if (
          message.keys.some(
            (deletedProjectId) => deletedProjectId === storedProjectId,
          )
        ) {
          localStorage.removeItem(STORE_PROJECT_ID);
          localStorage.removeItem(STORE_PACKAGE_ID);
        }
      }
    });
  }
}

async function listenToUserUpdates(
  cacheDataLoaded: Promise<unknown>,
  cacheEntryRemoved: Promise<unknown>,
  listener: Listener,
  cleanup?: () => void,
): Promise<void> {
  try {
    await cacheDataLoaded;

    if (!userUpdates) {
      createUserConnection();
    }

    userUpdates?.addEventListener("message", listener);
  } catch {
    // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
    // in which case `cacheDataLoaded` will throw
  }

  await cacheEntryRemoved;
  userUpdates?.removeEventListener("message", listener);

  if (cleanup) cleanup();
}

async function listenToGroupUpdates(
  cacheDataLoaded: Promise<unknown>,
  cacheEntryRemoved: Promise<unknown>,
  listener: Listener,
  cleanup?: () => void,
): Promise<void> {
  try {
    await cacheDataLoaded;

    groupUpdates?.addEventListener("message", listener);
  } catch {
    // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
    // in which case `cacheDataLoaded` will throw
  }

  await cacheEntryRemoved;
  groupUpdates?.removeEventListener("message", listener);

  if (cleanup) cleanup();
}

export {
  createGroupConnection,
  createUserConnection,
  listenToGroupUpdates,
  listenToUserUpdates,
};
